<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  accountName: { type: String, default: '' },
  logoUrl: { type: String, default: '' },
  website: { type: String, default: '' },
  fields: { type: Object, default: () => ({}) },
  enriching: { type: Boolean, default: false },
});

const { t } = useI18n();

const subtitle = computed(() => {
  if (!props.website) return t('ONBOARDING_V2.STAGE.BRAND.ADD_WEBSITE');
  if (props.enriching)
    return t('ONBOARDING_V2.STAGE.BRAND.READING', { website: props.website });
  return t('ONBOARDING_V2.STAGE.BRAND.ENRICHED', { website: props.website });
});

const chips = computed(() =>
  [
    ['role', 'ROLE'],
    ['language', 'LANGUAGE'],
    ['timezone', 'TIMEZONE'],
    ['industry', 'INDUSTRY'],
    ['companySize', 'COMPANY_SIZE'],
    ['referralSource', 'REFERRAL_SOURCE'],
  ].map(([field, labelKey]) => ({
    field,
    value: props.fields[field] || '',
    label: t(`ONBOARDING_V2.STAGE.BRAND.CHIPS.${labelKey}`),
  }))
);
</script>

<template>
  <div class="absolute inset-0 grid place-items-center">
    <Transition
      appear
      enter-active-class="motion-safe:transition-all motion-safe:duration-500 motion-safe:ease-out"
      enter-from-class="opacity-0 translate-y-6 scale-95"
    >
      <div
        class="relative w-80 rounded-[1.125rem] bg-white px-5 py-5 shadow-[0_24px_60px_-24px_rgba(30,20,70,0.5)]"
      >
        <!-- Header: logo + name + website state -->
        <div class="mb-3.5 flex items-center gap-3">
          <img
            v-if="logoUrl"
            :src="logoUrl"
            :alt="accountName"
            class="h-[2.375rem] w-[2.375rem] rounded-[0.625rem] object-cover"
          />
          <div
            v-else
            class="h-[2.375rem] w-[2.375rem] rounded-[0.625rem] bg-[linear-gradient(135deg,#6e56cf,#c94f9b)]"
          />
          <div class="min-w-0">
            <p class="truncate text-[0.9375rem] font-bold text-[#1b1926]">
              {{ accountName }}
            </p>
            <p class="truncate text-xs text-[#6f6c7a]">
              {{ subtitle }}
            </p>
          </div>
        </div>

        <!-- Field chips -->
        <div class="flex flex-wrap gap-1.5">
          <span
            v-for="chip in chips"
            :key="chip.field"
            class="rounded-full px-2.5 py-1 text-[0.71875rem] font-semibold transition-all duration-300"
            :class="
              chip.value
                ? 'scale-100 bg-[#ece7fd] text-[#6e56cf] opacity-100'
                : 'scale-95 bg-[#f1eff8] text-[#6f6c7a] opacity-45'
            "
          >
            {{ chip.value || chip.label }}
          </span>
        </div>

        <!-- Scan beam while enriching -->
        <div
          v-show="enriching"
          class="pointer-events-none absolute inset-0 overflow-hidden rounded-[1.125rem]"
        >
          <div
            class="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,rgba(110,86,207,0.18),transparent)] motion-safe:animate-pulse"
          />
        </div>
      </div>
    </Transition>
  </div>
</template>
